import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import PendingActionsPanel from './PendingActionsPanel';
import SessionInventoryPanel from './SessionInventoryPanel';
import ToastStack from './ToastStack';
import useUndoActions from '../hooks/useUndoActions';
import { getApiBaseUrl } from '../apiBase';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { canManageSchedule, isScheduleViewOnly, canShowAllSchoolsScheduleToggle, isStudentScheduleViewer } from '../utils/scheduleAccess';
import { isCoachAccount } from '../utils/roles';
import { getActiveSchoolId, getActiveStudentId } from '../utils/activeSchool';
import ScheduleSessionDetailModal from './ScheduleSessionDetailModal';
import { formatMonth, formatWeekdayDate } from '../utils/dateFormat';
import { getShortWeekDays, getWeekDayLabel } from '../utils/dateLocale';
import AddressInput from './address/AddressInput';
import { useOnboardingPageStep } from '../onboarding/useOnboardingPageStep';

const API = getApiBaseUrl();
const DAY_IDS = [1, 2, 3, 4, 5, 6, 7];
const GROUP_COLORS = ['#2563eb', '#7c3aed', '#059669', '#dc2626', '#ea580c', '#0891b2', '#4f46e5', '#be123c', '#0f766e', '#4338ca'];

export default function ScheduleManager() {
    useOnboardingPageStep('view_schedule', ['Coach', 'Member']);
    const { t, i18n } = useTranslation(['scheduleManager', 'schedule', 'common', 'errors', 'messages']);
    const DAYS = useMemo(() => DAY_IDS.map((id) => {
        const fromI18n = t(`scheduleManager:days.${id}`, { defaultValue: '' });
        const name = fromI18n && !fromI18n.startsWith('scheduleManager:')
            ? fromI18n
            : (getShortWeekDays()[id - 1] ?? '?');
        return { id, name };
    }), [i18n.language, t]);
    const [school, setSchool] = useState(null);
    const [groups, setGroups] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [slots, setSlots] = useState([]);
    const [monthDate, setMonthDate] = useState(new Date());
    const [loading, setLoading] = useState(true);

    const [selectedRoomId, setSelectedRoomId] = useState('');
    const [selectedSlotId, setSelectedSlotId] = useState('');
    const [slotForm, setSlotForm] = useState({
        id: null, groupId: '', roomId: '', dayOfWeek: 1,
        startTime: '18:00', endTime: '19:00', validFrom: todayStr(), validTo: ''
    });
    const [slotDays, setSlotDays] = useState([1]);
    const [allSchoolsView, setAllSchoolsView] = useState(false);
    const [capacityWarnings, setCapacityWarnings] = useState({});
    const [mySessions, setMySessions] = useState([]);
    const [planSaving, setPlanSaving] = useState('');
    const [inventorySession, setInventorySession] = useState(null);
    const [membership, setMembership] = useState(null);
    const canManage = canManageSchedule();
    const viewOnly = isScheduleViewOnly();
    const showAllSchoolsToggle = canShowAllSchoolsScheduleToggle();
    const activeStudentId = getActiveStudentId();
    const [draggingSlotId, setDraggingSlotId] = useState('');
    const [draggingStaffId, setDraggingStaffId] = useState('');
    const [dragOverDateKey, setDragOverDateKey] = useState('');
    const [dragSourceDateKey, setDragSourceDateKey] = useState('');
    const [staffList, setStaffList] = useState([]);
    const [defaultStaffRole, setDefaultStaffRole] = useState('');
    const [moveSlotModal, setMoveSlotModal] = useState(null); // { slot, sourceDateKey, targetDate, targetDateKey }
    const [pickSlotForStaffModal, setPickSlotForStaffModal] = useState(null); // { staffId, dateKey, slots }
    const [sessionDetailModal, setSessionDetailModal] = useState(null); // { slotId, sessionDate, dateKey, schoolId, slot }
    const [dayOverflowModal, setDayOverflowModal] = useState(null); // { dateKey, slots }
    useEffect(() => {
        if (!defaultStaffRole) setDefaultStaffRole(t('scheduleManager:staffRoleCoach'));
    }, [t, defaultStaffRole]);

    const { pendingActions, toasts, scheduleUndoableAction, cancelPendingAction, executePendingActionNow, dismissToast } = useUndoActions(10000);

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => {
        let cancelled = false;
        loadAll(() => cancelled);
        return () => { cancelled = true; };
    }, [monthDate, allSchoolsView, i18n.language]);

    async function loadAll(isCancelled = () => false) {
        setLoading(true);
        try {
            const from = firstDayOfMonth(monthDate);
            const to = lastDayOfMonth(monthDate);

            if (showAllSchoolsToggle && allSchoolsView) {
                const slotsRes = await api.get('/schedule/my', { params: { from, to } });
                setSchool({ name: t('scheduleManager:allSchoolsName'), id: null });
                setGroups([]);
                setRooms([]);
                setStaffList([]);
                setSlots(slotsRes.data || []);
                setCapacityWarnings({});
                if (viewOnly) {
                    const sessRes = await api.get('/planned-attendance/my-sessions', { params: { from, to } });
                    setMySessions(sessRes.data || []);
                } else {
                    setMySessions([]);
                }
            } else {
                const schoolRes = await getSchoolForSession(api);
                const schoolId = getSchoolIdFromResponse(schoolRes);
                setSchool(schoolRes.data);

                const slotsRes = await api.get('/schedule', { params: { schoolId, from, to } });
                setSlots(slotsRes.data || []);

                if (canManage) {
                    const [groupsRes, roomsRes, staffRes, warnRes] = await Promise.all([
                        api.get(`/school/${schoolId}/groups`),
                        api.get(`/room?schoolId=${schoolId}`),
                        api.get('/staff', { params: { schoolId } }),
                        api.get('/planned-attendance/capacity-warnings', { params: { schoolId, from, to } })
                    ]);
                    setGroups(groupsRes.data || []);
                    setRooms(roomsRes.data || []);
                    setStaffList((staffRes.data || []).filter(s => s.isActive !== false));
                    const warnMap = {};
                    (warnRes.data || []).forEach(w => { warnMap[w.date] = w; });
                    setCapacityWarnings(warnMap);
                } else {
                    const sid = getActiveSchoolId();
                    if (isCoachAccount()) {
                        try {
                            const groupsRes = await api.get(`/school/${schoolId}/groups`);
                            setGroups(groupsRes.data || []);
                        } catch {
                            setGroups([]);
                        }
                    } else {
                        setGroups([]);
                    }
                    setRooms([]);
                    setStaffList([]);
                    setCapacityWarnings({});
                    let mem = null;
                    if (isStudentScheduleViewer() && sid && !allSchoolsView) {
                        try {
                            const memRes = await api.get('/student/me/membership', { params: { schoolId: sid } });
                            mem = memRes.data;
                            setMembership(mem);
                        } catch {
                            setMembership(null);
                        }
                    } else {
                        setMembership(null);
                    }
                    if (isStudentScheduleViewer() && mem?.canPlanAttendance !== false) {
                        const sessRes = await api.get('/planned-attendance/my-sessions', {
                            params: { from, to, schoolId: allSchoolsView ? undefined : sid }
                        });
                        setMySessions(sessRes.data || []);
                    } else {
                        setMySessions([]);
                    }
                }
            }
        } catch (err) {
            if (!isCancelled()) {
                alert(err.response?.status === 403 ? t('scheduleManager:noAccess') : t('scheduleManager:loadError'));
            }
        }
        if (!isCancelled()) setLoading(false);
    }

    async function requestRestore(message) {
        const sid = getActiveSchoolId();
        if (!sid) return;
        try {
            await api.post('/student/me/request-restore', { message: message || null }, { params: { schoolId: sid } });
            alert(t('scheduleManager:requestSent'));
            const memRes = await api.get('/student/me/membership', { params: { schoolId: sid } });
            setMembership(memRes.data);
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:requestError'));
        }
    }

    async function setSessionPlan(session, willAttend) {
        const studentId = activeStudentId || session.studentId;
        if (!studentId) {
            alert(t('scheduleManager:noStudentProfile'));
            return;
        }
        const key = `${session.groupId}-${session.sessionDate}`;
        setPlanSaving(key);
        try {
            await api.put('/planned-attendance', {
                studentId,
                groupId: session.groupId,
                sessionDate: session.sessionDate,
                willAttend
            });
            setMySessions(prev => prev.map(s =>
                s.groupId === session.groupId && s.sessionDate === session.sessionDate
                    ? { ...s, willAttend, hasExplicitPlan: true }
                    : s
            ));
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:saveError'));
        }
        setPlanSaving('');
    }

    async function handleSaveSlot(e) {
        e.preventDefault();
        if (viewOnly) return;
        if (!slotForm.groupId) return alert(t('scheduleManager:pickGroup'));
        try {
            const base = {
                groupId: parseInt(slotForm.groupId, 10),
                roomId: slotForm.roomId ? parseInt(slotForm.roomId, 10) : null,
                startTime: slotForm.startTime,
                endTime: slotForm.endTime,
                validFrom: slotForm.validFrom,
                validTo: slotForm.validTo || null
            };

            if (slotForm.id) {
                await api.put(`/schedule/${slotForm.id}`, {
                    ...base,
                    dayOfWeek: parseInt(slotForm.dayOfWeek, 10)
                });
            } else {
                const days = slotDays.length ? slotDays : [parseInt(slotForm.dayOfWeek, 10)];
                if (days.length === 1) {
                    await api.post('/schedule', { ...base, dayOfWeek: days[0] });
                } else {
                    await api.post('/schedule/batch', { ...base, daysOfWeek: days });
                }
            }

            resetSlotForm();
            loadAll();
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:slotSaveError'));
        }
    }

    function toggleSlotDay(dayId) {
        setSlotDays(prev => {
            const has = prev.includes(dayId);
            if (has) {
                const next = prev.filter(d => d !== dayId);
                return next.length ? next : [dayId];
            }
            return [...prev, dayId].sort((a, b) => a - b);
        });
    }

    async function handleDeactivateSlot(id) {
        const slot = slots.find(s => s.id === id);
        scheduleUndoableAction(t('scheduleManager:deactivateSlotUndo', { name: slot?.groupName || slot?.id || '' }), async () => {
            try {
                await api.post(`/schedule/${id}/deactivate`);
                if (selectedSlotId === String(id)) setSelectedSlotId('');
                resetSlotForm();
                loadAll();
            } catch (err) {
                alert(err.response?.data?.message || t('scheduleManager:slotDeactivateError'));
            }
        });
    }

    function resetSlotForm() {
        setSlotForm({
            id: null, groupId: '', roomId: '', dayOfWeek: 1,
            startTime: '18:00', endTime: '19:00', validFrom: todayStr(), validTo: ''
        });
        setSlotDays([1]);
    }

    function openSessionDetail(slot, dateKey) {
        setSessionDetailModal({
            slotId: slot.id,
            sessionDate: dateKey,
            dateKey,
            schoolId: slot.schoolId || school?.id,
            slot
        });
    }

    function pickSlotForEdit(slot) {
        setSelectedSlotId(String(slot.id));
        setSlotForm({
            id: slot.id,
            groupId: String(slot.groupId),
            roomId: slot.roomId ? String(slot.roomId) : '',
            dayOfWeek: slot.dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            validFrom: slot.validFrom,
            validTo: slot.validTo || ''
        });
    }

    function beginDropOnCalendarDay(targetDate, targetDateKey, daySlots, e) {
        e.preventDefault();
        setDragOverDateKey('');
        if (draggingStaffId) {
            const sid = draggingStaffId;
            setDraggingStaffId('');
            const staffId = parseInt(sid, 10);
            const list = daySlots || [];
            if (list.length === 0) {
                alert(t('scheduleManager:dragNoSessions'));
                return;
            }
            if (list.length === 1) {
                assignStaffToSlot(staffId, list[0].id, defaultStaffRole);
                return;
            }
            setPickSlotForStaffModal({ staffId, dateKey: targetDateKey, slots: list });
            return;
        }
        if (draggingSlotId) {
            const slot = slots.find(s => s.id === parseInt(draggingSlotId, 10));
            const sourceDateKey = dragSourceDateKey;
            setDraggingSlotId('');
            setDragSourceDateKey('');
            if (!slot || !sourceDateKey || sourceDateKey === targetDateKey) return;
            setMoveSlotModal({ slot, sourceDateKey, targetDate, targetDateKey });
        }
    }

    async function executeMoveSlotChoice(mode) {
        if (!moveSlotModal) return;
        const { slot, sourceDateKey, targetDate, targetDateKey } = moveSlotModal;
        const newDayOfWeek = toDayId(targetDate);
        setMoveSlotModal(null);
        try {
            if (mode === 'single') {
                await api.post(`/schedule/${slot.id}/move-single`, {
                    sourceDate: sourceDateKey,
                    targetDate: targetDateKey
                });
                if (selectedSlotId === String(slot.id)) {
                    resetSlotForm();
                    setSelectedSlotId('');
                }
            } else {
                if (slot.dayOfWeek === newDayOfWeek) return;
                await api.put(`/schedule/${slot.id}`, {
                    groupId: slot.groupId,
                    roomId: slot.roomId,
                    dayOfWeek: newDayOfWeek,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    validFrom: slot.validFrom,
                    validTo: slot.validTo || null
                });
                if (selectedSlotId === String(slot.id)) {
                    setSlotForm(prev => ({ ...prev, dayOfWeek: newDayOfWeek }));
                }
            }
            await loadAll();
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:moveError', { date: targetDateKey }));
        }
    }

    async function assignStaffToSlot(staffId, slotId, roleInSlot) {
        try {
            await api.post('/staff/assign/slot', {
                staffId,
                slotId,
                roleInSlot: roleInSlot || defaultStaffRole || null
            });
            await loadAll();
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:staffAssignError'));
        }
    }

    async function unassignStaffFromSlot(staffId, slotId) {
        try {
            await api.delete('/staff/assign/slot', { params: { staffId, slotId } });
            await loadAll();
        } catch (err) {
            alert(err.response?.data?.message || t('scheduleManager:staffUnassignError'));
        }
    }

    const groupColorMap = useMemo(() => {
        const map = {};
        const ids = groups.length
            ? groups.map(g => g.id)
            : [...new Set(slots.map(s => s.groupId))];
        ids.forEach((id, idx) => { map[id] = GROUP_COLORS[idx % GROUP_COLORS.length]; });
        return map;
    }, [groups, slots]);

    const activeRooms = rooms.filter(r => r.isActive);
    const visibleSlots = selectedRoomId ? slots.filter(s => s.roomId === parseInt(selectedRoomId, 10)) : slots;

    const slotSelectOptions = useMemo(() => {
        return visibleSlots
            .slice()
            .sort((a, b) => (a.dayOfWeek - b.dayOfWeek) || a.startTime.localeCompare(b.startTime))
            .map(s => ({
                value: String(s.id),
                label: `${dayNameById(s.dayOfWeek)} ${s.startTime}-${s.endTime} · ${s.groupName}${s.roomName ? ` · ${s.roomName}` : ''}`,
                slot: s
            }));
    }, [visibleSlots]);

    const editingSlot = slotForm.id ? slots.find(s => s.id === slotForm.id) : null;

    const monthCells = useMemo(() => buildMonthCells(monthDate), [monthDate]);
    const eventsByDate = useMemo(() => {
        const map = {};
        monthCells.forEach(cell => { map[cell.dateKey] = []; });

        visibleSlots.forEach(slot => {
            monthCells.forEach(cell => {
                if (!cell.inCurrentMonth) return;
                if (!isSlotActiveOnDate(slot, cell.date)) return;
                if (toDayId(cell.date) !== slot.dayOfWeek) return;
                map[cell.dateKey].push(slot);
            });
        });

        Object.keys(map).forEach(k => map[k].sort((a, b) => a.startTime.localeCompare(b.startTime)));
        return map;
    }, [monthCells, visibleSlots]);

    let moveSlotDialog = null;
    if (moveSlotModal) {
        const { slot, sourceDateKey, targetDate, targetDateKey } = moveSlotModal;
        const newDow = toDayId(targetDate);
        const sameDow = slot.dayOfWeek === newDow;
        const srcLabel = formatDateLabel(sourceDateKey);
        const tgtLabel = formatDateLabel(targetDateKey);
        moveSlotDialog = (
            <div
                role="dialog"
                aria-modal="true"
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.48)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px'
                }}
                onClick={() => setMoveSlotModal(null)}
            >
                <div
                    style={{
                        background: '#fff',
                        borderRadius: '16px',
                        maxWidth: '440px',
                        width: '100%',
                        padding: '22px',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.18)'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>{t('scheduleManager:moveHowTo')}</h3>
                    <p style={{ margin: '0 0 6px', color: '#475569', fontSize: '14px', lineHeight: 1.45 }}>
                        <strong>{slot.groupName}</strong> · {slot.startTime}–{slot.endTime}
                    </p>
                    <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '13px' }}>
                        {t('scheduleManager:moveFromTo', { from: srcLabel, to: tgtLabel })}
                    </p>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={() => executeMoveSlotChoice('single')}
                            style={{ ...primaryBtnStyle, width: '100%', padding: '12px 14px', textAlign: 'left' }}
                        >
                            {t('scheduleManager:moveDateOnly')}
                            <div style={{ fontSize: '12px', fontWeight: 400, opacity: 0.92, marginTop: '4px' }}>
                                {t('scheduleManager:moveDateOnlyHint')}
                            </div>
                        </button>
                        <button
                            type="button"
                            disabled={sameDow}
                            onClick={() => executeMoveSlotChoice('template')}
                            style={{
                                ...primaryBtnStyle,
                                width: '100%',
                                padding: '12px 14px',
                                textAlign: 'left',
                                background: sameDow ? '#cbd5e1' : '#059669',
                                cursor: sameDow ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {t('scheduleManager:moveTemplate')}
                            <div style={{ fontSize: '12px', fontWeight: 400, opacity: 0.92, marginTop: '4px' }}>
                                {sameDow
                                    ? t('scheduleManager:moveSameWeekday')
                                    : t('scheduleManager:moveChangeWeekday')}
                            </div>
                        </button>
                        <button type="button" onClick={() => setMoveSlotModal(null)} style={{ ...ghostBtnStyle, width: '100%' }}>
                            {t('scheduleManager:cancel')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const scheduleSubtitle = viewOnly
        ? (allSchoolsView ? t('scheduleManager:subtitleAllGroups') : t('scheduleManager:subtitleCoach', { school: school?.name || '' }))
        : t('scheduleManager:subtitleOwner', { school: school?.name || '' });

    if (loading) {
        return (
            <OwnerPageLayout title={t('scheduleManager:title')} maxWidth={1220}>
                <div className="sp-loading-page">{t('scheduleManager:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <>
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
            <OwnerPageLayout title={t('scheduleManager:title')} subtitle={scheduleSubtitle} maxWidth={1220}>
                <PendingActionsPanel
                    pendingActions={pendingActions}
                    onCancel={cancelPendingAction}
                    onExecuteNow={executePendingActionNow}
                    containerStyle={{ ...cardStyle, border: '1px solid #fde68a', background: '#fffbeb', marginBottom: '10px' }}
                    rowButtonGhostStyle={ghostBtnStyle}
                    rowButtonDangerStyle={dangerBtnStyle}
                />
                {viewOnly && membership?.isArchived && (
                    <div style={{
                        marginTop: '12px', padding: '14px 16px', borderRadius: '12px',
                        background: '#fff7ed', border: '1px solid #fdba74', fontSize: '14px'
                    }}>
                        <strong>{t('scheduleManager:archivedBanner')}</strong>
                        <p style={{ margin: '8px 0 0', color: '#9a3412', lineHeight: 1.45 }}>
                            {t('scheduleManager:archivedHint')}
                        </p>
                        {membership.pendingRestoreApplication ? (
                            <p style={{ margin: '10px 0 0', fontWeight: 600, color: '#c2410c' }}>
                                {t('scheduleManager:restorePending')}
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    const msg = window.prompt(t('scheduleManager:ownerCommentPrompt'));
                                    if (msg !== null) requestRestore(msg);
                                }}
                                style={{
                                    marginTop: '12px', padding: '10px 18px', borderRadius: '10px',
                                    border: 'none', background: '#ea580c', color: 'white',
                                    fontWeight: 700, cursor: 'pointer'
                                }}
                            >
                                {t('scheduleManager:requestRestore')}
                            </button>
                        )}
                        <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#78716c' }}>
                            {t('scheduleManager:archiveViewOnly')}
                        </p>
                    </div>
                )}
                {viewOnly && isStudentScheduleViewer() && membership && !membership.exists && !allSchoolsView && (
                    <div style={{
                        marginTop: '12px', padding: '14px 16px', borderRadius: '12px',
                        background: '#eff6ff', border: '1px solid #93c5fd', fontSize: '14px'
                    }}>
                        <p style={{ margin: 0 }}>
                            {t('scheduleManager:notEnrolled')}{' '}
                            <a href="/apply" style={{ color: '#2563eb', fontWeight: 600 }}>{t('scheduleManager:applyLink')}</a>
                        </p>
                    </div>
                )}
                {showAllSchoolsToggle && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={allSchoolsView}
                            onChange={e => setAllSchoolsView(e.target.checked)}
                        />
                        {t('scheduleManager:showAllGroups')}
                    </label>
                )}


                <div style={{
                    marginTop: '16px',
                    display: 'grid',
                    gridTemplateColumns: canManage ? '1.35fr 1fr' : '1fr',
                    gap: '14px'
                }}>
                    <div style={cardStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '17px' }}>{t('scheduleManager:monthCalendar')}</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button type="button" onClick={() => setMonthDate(addMonth(monthDate, -1))} style={miniBtnStyle} aria-label={t('scheduleManager:prevMonth')}>‹</button>
                                <strong>{formatMonth(monthDate)}</strong>
                                <button type="button" onClick={() => setMonthDate(addMonth(monthDate, 1))} style={miniBtnStyle} aria-label={t('scheduleManager:nextMonth')}>›</button>
                            </div>
                        </div>

                        {canManage && (
                        <div style={{
                            marginBottom: '10px',
                            padding: '10px',
                            borderRadius: '10px',
                            border: '1px dashed #cbd5e1',
                            background: '#f8fafc'
                        }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>{t('scheduleManager:dragCalendar')}</div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{t('scheduleManager:noStaff')}</span>
                                <input
                                    value={defaultStaffRole}
                                    onChange={e => setDefaultStaffRole(e.target.value)}
                                    style={{ ...inputStyle, display: 'inline-block', width: '120px', marginLeft: '6px', padding: '4px 8px', fontSize: '12px' }}
                                />
                            </div>
                            {staffList.length === 0 ? (
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>{t('scheduleManager:noStaffDirectory')}</div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {staffList.map(s => (
                                        <div
                                            key={s.id}
                                            draggable
                                            onDragStart={e => {
                                                e.dataTransfer.setData('text/plain', `staff:${s.id}`);
                                                e.dataTransfer.effectAllowed = 'copy';
                                                setDraggingStaffId(String(s.id));
                                            }}
                                            onDragEnd={() => {
                                                setDraggingStaffId('');
                                                setDragOverDateKey('');
                                            }}
                                            style={{
                                                fontSize: '12px',
                                                padding: '6px 10px',
                                                borderRadius: '999px',
                                                background: draggingStaffId === String(s.id) ? '#dbeafe' : '#fff',
                                                border: '1px solid #93c5fd',
                                                cursor: 'grab',
                                                fontWeight: 600,
                                                color: '#1e3a8a'
                                            }}
                                            title={t('scheduleManager:dragHint')}
                                        >
                                            {s.fullName}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                {t('scheduleManager:slotsCount', { count: visibleSlots.length })}
                            </div>
                            {canManage && (
                                <>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        {t('scheduleManager:filterRooms')}
                                    </div>
                                    <select
                                        value={selectedRoomId}
                                        onChange={e => {
                                            setSelectedRoomId(e.target.value);
                                            setSelectedSlotId('');
                                        }}
                                        style={selectStyle}
                                    >
                                        <option value="">{t('scheduleManager:allRooms')}</option>
                                        {rooms.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} {r.isActive ? '' : t('scheduleManager:inactive')}
                                            </option>
                                        ))}
                                    </select>
                                    <Link to="/school/venues" style={{ fontSize: 12, color: '#2563eb', whiteSpace: 'nowrap' }}>
                                        {t('scheduleManager:roomsLink')}
                                    </Link>
                                </>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px' }}>
                            {DAYS.map(d => (
                                <div key={d.id} style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#475569', padding: '4px 0' }}>
                                    {d.name}
                                </div>
                            ))}
                            {monthCells.map(cell => (
                                <div
                                    key={cell.dateKey}
                                    onDragOver={canManage ? (e => {
                                        if (draggingSlotId || draggingStaffId) {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = draggingStaffId ? 'copy' : 'move';
                                        }
                                        if (dragOverDateKey !== cell.dateKey) setDragOverDateKey(cell.dateKey);
                                    }) : undefined}
                                    onDragLeave={canManage ? () => {
                                        if (dragOverDateKey === cell.dateKey) setDragOverDateKey('');
                                    } : undefined}
                                    onDrop={canManage ? (e => beginDropOnCalendarDay(cell.date, cell.dateKey, eventsByDate[cell.dateKey] || [], e)) : undefined}
                                    style={{
                                        minHeight: '118px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        padding: '6px',
                                        background: cell.inCurrentMonth ? '#fff' : '#f8fafc',
                                        opacity: cell.inCurrentMonth ? 1 : 0.65,
                                        outline: dragOverDateKey === cell.dateKey ? '2px solid #3b82f6' : 'none',
                                        outlineOffset: dragOverDateKey === cell.dateKey ? '-2px' : 0
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: cell.isToday ? '#2563eb' : '#334155' }}>
                                            {cell.date.getDate()}
                                        </span>
                                        {capacityWarnings[cell.dateKey] && (
                                            <span
                                                title={capacityWarnings[cell.dateKey].message}
                                                style={{
                                                    fontSize: '14px', lineHeight: 1, cursor: 'help',
                                                    filter: 'drop-shadow(0 0 1px #fff)'
                                                }}
                                            >
                                                ⚠️
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'grid', gap: '4px' }}>
                                        {(eventsByDate[cell.dateKey] || []).slice(0, 3).map(slot => (
                                            <button
                                                key={`${cell.dateKey}_${slot.id}`}
                                                type="button"
                                                draggable={canManage}
                                                onDragStart={canManage ? (e => {
                                                    e.dataTransfer.setData('text/plain', String(slot.id));
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    setDraggingSlotId(String(slot.id));
                                                    setDragSourceDateKey(cell.dateKey);
                                                }) : undefined}
                                                onDragOver={canManage ? (e => {
                                                    if (draggingStaffId) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        e.dataTransfer.dropEffect = 'copy';
                                                    }
                                                }) : undefined}
                                                onDrop={canManage ? (e => {
                                                    if (!draggingStaffId) return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const sid = draggingStaffId;
                                                    setDraggingStaffId('');
                                                    setDragOverDateKey('');
                                                    assignStaffToSlot(parseInt(sid, 10), slot.id, defaultStaffRole);
                                                }) : undefined}
                                                onDragEnd={canManage ? () => {
                                                    setDraggingSlotId('');
                                                    setDragOverDateKey('');
                                                    setDragSourceDateKey('');
                                                } : undefined}
                                                onClick={() => openSessionDetail(slot, cell.dateKey)}
                                                style={{
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '4px 6px',
                                                    textAlign: 'left',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                    background: `${groupColorMap[slot.groupId] || '#64748b'}22`,
                                                    color: groupColorMap[slot.groupId] || '#334155',
                                                    opacity: draggingSlotId === String(slot.id) ? 0.6 : 1
                                                }}
                                                title={`${slot.schoolName ? slot.schoolName + ' · ' : ''}${slot.groupName} · ${slot.startTime}-${slot.endTime} · ${slot.roomName || t('scheduleManager:noRoom')}`}
                                            >
                                                <div>
                                                    <strong>{slot.startTime}</strong> {slot.groupName}
                                                    {slot.schoolName && <span style={{ fontSize: '9px', color: '#64748b' }}> · · {slot.schoolName}</span>}
                                                </div>
                                                {slot.roomName && (
                                                    <div style={{ fontSize: '9px', color: '#475569', marginTop: '1px' }}>
                                                        🏢 {slot.roomName}
                                                    </div>
                                                )}
                                                {(slot.staff && slot.staff.length > 0) && (
                                                    <div style={{ fontSize: '9px', color: '#475569', marginTop: '2px', lineHeight: 1.2 }}>
                                                        {slot.staff.slice(0, 2).map(st => st.fullName).join(' · ')}
                                                        {slot.staff.length > 2 ? ` +${slot.staff.length - 2}` : ''}
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                        {(eventsByDate[cell.dateKey] || []).length > 3 && (
                                            <button
                                                type="button"
                                                onClick={() => setDayOverflowModal({
                                                    dateKey: cell.dateKey,
                                                    slots: eventsByDate[cell.dateKey]
                                                })}
                                                style={{
                                                    border: 'none', background: 'transparent', padding: 0,
                                                    fontSize: '10px', color: '#2563eb', cursor: 'pointer', textAlign: 'left'
                                                }}
                                            >
                                                {t('scheduleManager:moreEvents', { count: (eventsByDate[cell.dateKey] || []).length - 3 })}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {viewOnly && membership?.canPlanAttendance && mySessions.length > 0 && (
                            <div style={{ ...cardStyle, marginTop: '14px' }}>
                                <h3 style={{ margin: '0 0 10px', fontSize: '16px' }}>{t('scheduleManager:upcomingDays')}</h3>
                                <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#64748b' }}>
                                    {t('scheduleManager:upcomingHint')}
                                </p>
                                <div style={{ display: 'grid', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                                    {mySessions.map(s => {
                                        const key = `${s.groupId}-${s.sessionDate}`;
                                        const saving = planSaving === key;
                                        return (
                                            <div
                                                key={`${key}-${s.slotId}`}
                                                style={{
                                                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
                                                    padding: '10px', borderRadius: '10px', border: '1px solid #e5e7eb',
                                                    background: s.willAttend ? '#f0fdf4' : '#fef2f2'
                                                }}
                                            >
                                                <div style={{ flex: '1 1 200px', fontSize: '13px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => openSessionDetail(
                                                            { id: s.slotId, schoolId: s.schoolId, groupId: s.groupId, groupName: s.groupName },
                                                            s.sessionDate
                                                        )}
                                                        style={{
                                                            border: 'none', background: 'transparent', padding: 0,
                                                            textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: 'inherit'
                                                        }}
                                                    >
                                                        <strong>{s.sessionDate}</strong> {s.startTime}–{s.endTime}
                                                        {' · '}{s.groupName}
                                                        {s.roomName && <span style={{ color: '#64748b' }}> · 🏢 {s.roomName}</span>}
                                                        {s.schoolName && <span style={{ color: '#64748b' }}> ({s.schoolName})</span>}
                                                    </button>
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        type="button"
                                                        disabled={saving}
                                                        onClick={() => setSessionPlan(s, true)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '8px', border: 'none',
                                                            background: s.willAttend ? '#16a34a' : '#e5e7eb',
                                                            color: s.willAttend ? '#fff' : '#334155',
                                                            fontWeight: 600, fontSize: '12px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('scheduleManager:willAttend')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={saving}
                                                        onClick={() => setSessionPlan(s, false)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '8px', border: 'none',
                                                            background: !s.willAttend ? '#dc2626' : '#e5e7eb',
                                                            color: !s.willAttend ? '#fff' : '#334155',
                                                            fontWeight: 600, fontSize: '12px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('scheduleManager:wontAttend')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={saving}
                                                        onClick={() => setInventorySession(s)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '8px', border: '1px solid #EB3333',
                                                            background: '#fff', color: '#EB3333',
                                                            fontWeight: 600, fontSize: '12px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('scheduleManager:inventory')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {viewOnly && membership?.canPlanAttendance && mySessions.length === 0 && !loading && (
                            <p style={{ marginTop: '12px', fontSize: '13px', color: '#94a3b8' }}>
                                {t('scheduleManager:noSessionsMonth')}
                            </p>
                        )}
                        {viewOnly && membership?.canPlanAttendance && slots.length > 0 && mySessions.length === 0 && (
                            <p style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                {t('scheduleManager:calendarHint')}
                            </p>
                        )}
                    </div>

                    {canManage && (
                    <div style={{ display: 'grid', gap: '14px' }}>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '17px' }}>{t('scheduleManager:slotFormTitle')}</h3>
                                <span style={badgeStyle}>{visibleSlots.length}</span>
                            </div>

                            <select
                                value={selectedSlotId}
                                onChange={e => {
                                    setSelectedSlotId(e.target.value);
                                    const option = slotSelectOptions.find(o => o.value === e.target.value);
                                    if (option) pickSlotForEdit(option.slot);
                                    else resetSlotForm();
                                }}
                                style={{ ...selectStyle, marginBottom: '8px' }}
                            >
                                <option value="">{t('scheduleManager:pickSlotEdit')}</option>
                                {slotSelectOptions.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>

                            <form onSubmit={handleSaveSlot} style={{ display: 'grid', gap: '8px' }}>
                                <select value={slotForm.groupId} onChange={e => setSlotForm({ ...slotForm, groupId: e.target.value })} required style={selectStyle}>
                                    <option value="">{t('scheduleManager:groupPlaceholder')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                                <select value={slotForm.roomId} onChange={e => setSlotForm({ ...slotForm, roomId: e.target.value })} style={selectStyle}>
                                    <option value="">{t('scheduleManager:allRooms')}</option>
                                    {activeRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                                {slotForm.id ? (
                                    <select value={slotForm.dayOfWeek} onChange={e => setSlotForm({ ...slotForm, dayOfWeek: e.target.value })} style={selectStyle}>
                                        {DAYS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                ) : (
                                    <div>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>{t('scheduleManager:weekdays')}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {DAYS.map(d => {
                                                const on = slotDays.includes(d.id);
                                                return (
                                                    <button
                                                        key={d.id}
                                                        type="button"
                                                        onClick={() => toggleSlotDay(d.id)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '8px',
                                                            border: on ? '2px solid #EB3333' : '1px solid var(--sp-gray-200)',
                                                            background: on ? '#f3eeff' : '#fff',
                                                            fontWeight: on ? 700 : 500,
                                                            cursor: 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        {d.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <input type="time" value={slotForm.startTime} onChange={e => setSlotForm({ ...slotForm, startTime: e.target.value })} style={inputStyle} />
                                    <input type="time" value={slotForm.endTime} onChange={e => setSlotForm({ ...slotForm, endTime: e.target.value })} style={inputStyle} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <input type="date" value={slotForm.validFrom} onChange={e => setSlotForm({ ...slotForm, validFrom: e.target.value })} style={inputStyle} />
                                    <input type="date" value={slotForm.validTo} onChange={e => setSlotForm({ ...slotForm, validTo: e.target.value })} style={inputStyle} />
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="submit" style={primaryBtnStyle}>{slotForm.id ? t('scheduleManager:saveSlot') : t('scheduleManager:createSlot')}</button>
                                    {slotForm.id && <button type="button" onClick={() => handleDeactivateSlot(slotForm.id)} style={dangerBtnStyle}>{t('scheduleManager:deleteSlot')}</button>}
                                    {slotForm.id && <button type="button" onClick={() => { setSelectedSlotId(''); resetSlotForm(); }} style={ghostBtnStyle}>{t('scheduleManager:reset')}</button>}
                                </div>
                            </form>

                            {slotForm.id && editingSlot && (
                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>{t('scheduleManager:staffInSlot')}</div>
                                    <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 8px', lineHeight: 1.35 }}>
                                        {t('scheduleManager:staffInSlotHint')}
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                                        {(editingSlot.staff || []).length === 0 ? (
                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{t('scheduleManager:noStaff')}</span>
                                        ) : (
                                            (editingSlot.staff || []).map(st => (
                                                <span
                                                    key={st.staffMemberId}
                                                    style={{
                                                        fontSize: '12px',
                                                        padding: '4px 8px',
                                                        borderRadius: '8px',
                                                        background: '#eef2ff',
                                                        color: '#3730a3',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    {st.fullName}
                                                    {st.roleInSlot ? <span style={{ color: '#64748b' }}>({st.roleInSlot})</span> : null}
                                                    <button
                                                        type="button"
                                                        onClick={() => unassignStaffFromSlot(st.staffMemberId, editingSlot.id)}
                                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, fontSize: '14px', lineHeight: 1, color: '#94a3b8' }}
                                                        title={t('scheduleManager:unassign')}
                                                    >
                                                        ?
                                                    </button>
                                                </span>
                                            ))
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <select
                                            defaultValue=""
                                            onChange={e => {
                                                const v = e.target.value;
                                                e.target.value = '';
                                                if (v) assignStaffToSlot(parseInt(v, 10), editingSlot.id, defaultStaffRole);
                                            }}
                                            style={{ ...selectStyle, maxWidth: '100%', flex: '1 1 160px' }}
                                        >
                                            <option value="">{t('scheduleManager:addStaff')}</option>
                                            {staffList
                                                .filter(s => !(editingSlot.staff || []).some(x => x.staffMemberId === s.id))
                                                .map(s => (
                                                    <option key={s.id} value={s.id}>{s.fullName}</option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {groups.map(g => (
                                    <span key={g.id} style={{
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        borderRadius: '999px',
                                        background: `${groupColorMap[g.id]}22`,
                                        color: groupColorMap[g.id]
                                    }}>
                                        {g.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    )}
                </div>

            {moveSlotDialog}

            {pickSlotForStaffModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.48)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                    onClick={() => setPickSlotForStaffModal(null)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: '16px',
                            maxWidth: '400px',
                            width: '100%',
                            padding: '22px',
                            boxShadow: '0 24px 60px rgba(0,0,0,0.18)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ margin: '0 0 10px', fontSize: '17px' }}>{t('scheduleManager:assignStaffTitle')}</h3>
                        <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#64748b' }}>
                            {t('scheduleManager:assignStaffHint', { date: formatDateLabel(pickSlotForStaffModal.dateKey) })}
                        </p>
                        <div style={{ display: 'grid', gap: '8px' }}>
                            {pickSlotForStaffModal.slots.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => {
                                        assignStaffToSlot(pickSlotForStaffModal.staffId, s.id, defaultStaffRole);
                                        setPickSlotForStaffModal(null);
                                    }}
                                    style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        padding: '10px 12px',
                                        textAlign: 'left',
                                        background: '#f8fafc',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    <strong>{s.startTime}</strong> · {s.groupName}
                                    {s.roomName ? <span style={{ color: '#64748b' }}> · {s.roomName}</span> : null}
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={() => setPickSlotForStaffModal(null)} style={{ ...ghostBtnStyle, width: '100%', marginTop: '12px' }}>
                            {t('scheduleManager:close')}</button>
                    </div>
                </div>
            )}
            {inventorySession && (
                <SessionInventoryPanel
                    session={inventorySession}
                    api={api}
                    onClose={() => setInventorySession(null)}
                />
            )}

            <ScheduleSessionDetailModal
                open={!!sessionDetailModal}
                slotId={sessionDetailModal?.slotId}
                sessionDate={sessionDetailModal?.sessionDate}
                schoolId={sessionDetailModal?.schoolId}
                token={token}
                canManage={canManage}
                isCoach={isCoachAccount()}
                onClose={() => setSessionDetailModal(null)}
                onEdit={(detail) => {
                    const slot = sessionDetailModal?.slot || slots.find(s => s.id === detail.slotId);
                    if (slot) pickSlotForEdit(slot);
                    setSessionDetailModal(null);
                }}
                onAttendance={(detail) => {
                    window.location.href = `/attendance?date=${detail.sessionDate}&groupId=${detail.groupId}`;
                }}
            />

            {dayOverflowModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.48)',
                        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                    }}
                    onClick={() => setDayOverflowModal(null)}
                >
                    <div
                        style={{
                            background: '#fff', borderRadius: '16px', maxWidth: '420px', width: '100%',
                            padding: '22px', boxShadow: '0 24px 60px rgba(0,0,0,0.18)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ margin: '0 0 10px', fontSize: '17px' }}>
                            {t('scheduleManager:daySessions', { date: formatDateLabel(dayOverflowModal.dateKey) })}
                        </h3>
                        <div style={{ display: 'grid', gap: '8px', maxHeight: '360px', overflowY: 'auto' }}>
                            {dayOverflowModal.slots.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => {
                                        openSessionDetail(s, dayOverflowModal.dateKey);
                                        setDayOverflowModal(null);
                                    }}
                                    style={{
                                        border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px',
                                        textAlign: 'left', background: '#f8fafc', cursor: 'pointer', fontSize: '14px'
                                    }}
                                >
                                    <strong>{s.startTime}–{s.endTime}</strong> {s.groupName}
                                    {s.roomName ? <span style={{ color: '#64748b' }}> · 🏢 {s.roomName}</span> : null}
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={() => setDayOverflowModal(null)} style={{ ...ghostBtnStyle, width: '100%', marginTop: '12px' }}>
                            {t('scheduleManager:close')}</button>
                    </div>
                </div>
            )}
            </OwnerPageLayout>
        </>
    );
}

const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '14px' };
const inputStyle = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', boxSizing: 'border-box' };
const selectStyle = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', background: '#fff', boxSizing: 'border-box' };
const primaryBtnStyle = { border: 'none', borderRadius: '9px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', background: 'var(--sp-primary)', color: '#fff', fontWeight: 700 };
const dangerBtnStyle = { border: 'none', borderRadius: '9px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', background: '#b91c1c', color: '#fff', fontWeight: 700 };
const ghostBtnStyle = { border: '1px solid #cbd5e1', borderRadius: '9px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', background: '#fff', color: '#334155', fontWeight: 700 };
const miniBtnStyle = { border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', padding: '4px 8px', cursor: 'pointer' };
const badgeStyle = { background: '#eef2ff', color: '#4338ca', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 700 };

function todayStr() {
    return new Date().toISOString().split('T')[0];
}

function firstDayOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}

function lastDayOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
}

function addMonth(d, delta) {
    return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function buildMonthCells(date) {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const startShift = (toDayId(start) + 6) % 7; // Monday-first
    const cells = [];

    for (let i = 0; i < startShift; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() - (startShift - i));
        cells.push(cellFromDate(d, false));
    }
    for (let day = 1; day <= end.getDate(); day++) {
        const d = new Date(date.getFullYear(), date.getMonth(), day);
        cells.push(cellFromDate(d, true));
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
        const last = new Date(cells[cells.length - 1].date);
        last.setDate(last.getDate() + 1);
        cells.push(cellFromDate(last, false));
    }
    return cells;
}

function cellFromDate(date, inCurrentMonth) {
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();
    return { date, inCurrentMonth, isToday, dateKey: formatDateKey(date) };
}

function formatDateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function toDayId(d) {
    const js = d.getDay(); // 0..6 (Sun..Sat)
    return js === 0 ? 7 : js; // 1..7 (Mon..Sun)
}

function isSlotActiveOnDate(slot, date) {
    const d = formatDateKey(date);
    if (d < slot.validFrom) return false;
    if (slot.validTo && d > slot.validTo) return false;
    return true;
}

function dayNameById(id) {
    return getWeekDayLabel(id, 'short') || '?';
}

function formatDateLabel(dateKey) {
    if (!dateKey) return '';
    return formatWeekdayDate(`${dateKey}T12:00:00`);
}
